import React, { useEffect, useState } from "react";
import { MovePersonButton } from "@/components/nds/MovePerson";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { ResidentLeaderControls } from "@/components/console/ResidentLeader";
import { TeaPhoneTag, TeaPhoneToggle } from "@/components/console/PreviewAs";
import { TestAccountBanner } from "@/components/nds/windows/resident/TestAccounts";
import { ArrowLeft, Loader2, User2 } from "lucide-react";
import { reApi, errMsg, INTAKE_LABEL, INTAKE_TONE } from "@/lib/residentApi";
import { Pill } from "./ui";
import ProfileTab from "./ProfileTab";
import NotesTab from "./NotesTab";
import MemoBoardTab from "./MemoBoardTab";
import CheckInsTab from "./CheckInsTab";
import PhaseTab from "./PhaseTab";
import DocumentsTab from "./DocumentsTab";
import VocationTab from "./VocationTab";
import RoadmapTab from "./RoadmapTab";
import GedTab from "./GedTab";
import CalendarTab from "./CalendarTab";
import LifeSkillsTab from "./LifeSkillsTab";
import FinancialTab from "./FinancialTab";
import PartnerFinance from "./PartnerFinance";
import ReviewsTab from "./ReviewsTab";
import ImpactTab from "./ImpactTab";
import LifePassTab from "./LifePassTab";
import { ArchivePanel } from "./ArchivePanel";
import ResidentArchiveTab from "./ResidentArchiveTab";
import { LifePassPin } from "@/components/nds/windows/pillar/LifePassPin";
import ResidentCards from "@/components/nds/windows/resident/ResidentCards";
import { StartSessionButton } from "@/components/nds/StartSessionButton";
import { useAuth } from "@/lib/auth";

const TABS = [
  ["profile", "Profile", ProfileTab],
  ["lifepass", "PACT Life Pass", LifePassTab],
  ["vocation", "Vocation", VocationTab],
  ["roadmap", "Roadmap", RoadmapTab],
  ["ged", "GED Pathway", GedTab],
  ["calendar", "Calendar", CalendarTab],
  ["lifeskills", "Life Skills", LifeSkillsTab],
  ["financial", "Financial", FinancialTab],
  ["notes", "Notes", NotesTab],
  ["memos", "Memo Board", MemoBoardTab],
  ["checkins", "Check-ins", CheckInsTab],
  ["phase", "Phase", PhaseTab],
  ["documents", "Documents", DocumentsTab],
  ["reviews", "Reviews", ReviewsTab],
  ["impact", "Impact", ImpactTab],
  ["archive", "Archive", ResidentArchiveTab],
];
// Partners only get the cards their partner permissions cover; notes, memos and archive stay staff-only.
const PARTNER_PERM = { profile: "view_resident_profile", phase: "view_resident_profile", calendar: "view_resident_profile", checkins: "view_resident_profile",
  documents: "view_resident_profile", vocation: "view_resident_pillar", roadmap: "view_resident_pillar", lifeskills: "view_resident_pillar",
  reviews: "view_resident_pillar", impact: "view_resident_pillar", financial: "view_resident_finance" };

export default function ResidentDetail({ residentId, onBack, onChanged, focus }) {
  const [resident, setResident] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const granted = user?.partner_permissions || [];
  const leader = user?.role_title === "Resident Leader";  // works granted cards like staff (archive stays staff-only)
  const tabs = leader ? TABS.filter(([id]) => id !== "archive") : user?.role === "partner_user"
    ? TABS.filter(([id]) => granted.includes(PARTNER_PERM[id])).map(([id, l, C]) => [id, l, id === "financial" ? PartnerFinance : C]) : TABS;
  const [tab, setTab] = useState(focus?.tab || null);
  useEffect(() => setTab(focus?.tab || null), [residentId, focus?.tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [docCount, setDocCount] = useState(null);
  const load = () => {
    reApi.get(`/documents`, { resident_id: residentId }).then((d) => setDocCount(d.active_count)).catch(() => setDocCount(null));
    return reApi.get(`/residents/${residentId}`).then(setResident).catch((e) => setError(errMsg(e)));
  };
  useEffect(() => { load(); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { load(); onChanged?.(); };
  const isSuper = useIsSuper();
  const [, tabLabel, Active] = tabs.find(([id]) => id === tab) || [];
  const cards = !Active;
  const scroller = React.useRef(null);
  const lastTop = React.useRef(0);
  const box = () => {
    for (let el = scroller.current; el; el = el.parentElement) if (el.scrollHeight > el.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
    return document.scrollingElement;
  };
  const openTab = (id) => { const el = box(); if (el) lastTop.current = el.scrollTop; setTab(id); };
  React.useLayoutEffect(() => { const el = box(); if (el) el.scrollTop = cards ? lastTop.current : 0; }, [cards]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="resident-detail" className="flex flex-col h-full" style={{ minHeight: "520px" }}>
      <div className="px-5 pt-3 pb-3 border-b border-white/10">
        <button type="button" onClick={onBack} data-testid="resident-detail-back"
          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold hover:text-nex-goldbright">
          <ArrowLeft className="h-3.5 w-3.5" /> Resident list
        </button>
        {resident && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="h-11 w-11 rounded-full grid place-items-center bg-nex-gold/15 text-nex-gold border border-nex-gold/40">
              <User2 className="h-5 w-5" />
            </div>
            <div className="w-full sm:w-auto min-w-0 flex-1 basis-[calc(100%-3.5rem)] sm:basis-auto">
              <h3 data-testid="resident-detail-name" className="text-lg font-semibold text-nex-silver truncate">
                {resident.first_name} {resident.last_name}
                {resident.preferred_name && <span className="text-nex-muted text-sm font-normal"> “{resident.preferred_name}”</span>}
              </h3>
              <div className="flex flex-wrap gap-2 mt-1">
                <Pill tone={resident.status === "active" ? "green" : resident.status === "archived" ? "yellow" : "dim"} data-testid="resident-detail-status">{resident.status}</Pill>
                <Pill data-testid="resident-detail-phase">Phase {resident.current_phase}</Pill>
                {resident.tea_phone && <TeaPhoneTag id={resident.id} />}
                <Pill tone={INTAKE_TONE[resident.intake_status || "not_started"]} data-testid="resident-detail-intake">Intake · {INTAKE_LABEL[resident.intake_status || "not_started"]}</Pill>
                {docCount != null && <Pill tone="dim" data-testid="resident-detail-doc-count">{docCount} active doc{docCount === 1 ? "" : "s"}</Pill>}
              </div>
              <TestAccountBanner resident={resident} isSuper={isSuper} onChanged={refresh} />
            </div>
            <MovePersonButton kind="resident" id={resident.id} onDone={refresh} />
            <ResidentLeaderControls rid={resident.id} name={`${resident.first_name} ${resident.last_name}`} leader={!!resident.resident_leader} onChanged={refresh} />
            <TeaPhoneToggle rid={resident.id} on={!!resident.tea_phone} onChanged={refresh} />
            {resident.status === "active" && <StartSessionButton residentId={resident.id} name={`${resident.first_name} ${resident.last_name}`} />}
          </div>
        )}
        {resident && <ArchivePanel resident={resident} onChanged={refresh} />}
        {resident && tab === "profile" && resident.status !== "archived" && <div className="mt-3"><LifePassPin residentId={resident.id} /></div>}
      </div>
      <div ref={scroller} className="flex-1 overflow-y-auto p-5">
        {error ? <p className="text-red-300 text-sm">{error}</p>
          : !resident ? <div className="flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          : cards ? (tabs.length ? <ResidentCards residentId={residentId} tabs={tabs} onOpen={openTab} />
            : <p data-testid="resident-cards-none" className="text-sm text-nex-muted">You don't have access to any sections for this resident.</p>)
          : <>
            <button type="button" onClick={() => setTab(null)} data-testid="resident-cards-back"
              className="mb-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold hover:text-nex-goldbright transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> All cards · <span className="text-nex-silver">{tabLabel}</span>
            </button>
            <Active resident={resident} onChanged={refresh} focus={focus} />
          </>}
      </div>
    </div>
  );
}
